import { Line, LineConfig, PlotEvent } from '@ant-design/charts';
import { useLazyQuery, useQuery } from '@apollo/client';
import {
  GET_COMPANY_STATISTICS,
  GET_TOTAL_COMPANY_COUNT,
  TotalInstitutionData,
} from '@frontend/data-access';
import { getLinkLabel } from '@frontend/shared/util';
import { Card, DatePicker } from 'antd';
import { RangePickerProps } from 'antd/lib/date-picker';
import dayjs from 'dayjs';
import useTranslation from 'next-translate/useTranslation';
import { RefAttributes, useEffect, useMemo, useState } from 'react';

const { RangePicker } = DatePicker;

const COLOR_PLATE_10 = [
  '#00CC00',
  '#0273EA',
  '#B1BDC9',
  '#7AC4FC',
  '#C5F1BB',
  '#A3B1BF',
  '#8B8B8B',
  '#525355',
];

const TotalCompaniesStats = () => {
  const { t } = useTranslation('admin');
  const { t: tcommon } = useTranslation('common');
  const [data, setData] = useState<TotalInstitutionData[]>([]);
  const endDate = dayjs();
  const startDate = endDate.subtract(3, 'month');
  const [dates, setDates] = useState([
    startDate.format('YYYY-MM-DD'),
    endDate.format('YYYY-MM-DD'),
  ]);
  const [selectedItems, setSelectedItems] = useState<string[]>([]);

  const { data: dataTotal } = useQuery<{ totalCompanyCount: number }>(
    GET_TOTAL_COMPANY_COUNT
  );
  const [getData, { loading }] = useLazyQuery<{
    companyStatisticsGraph: {
      date: string;
      values: {
        count: number;
        type: string;
      };
    }[];
  }>(GET_COMPANY_STATISTICS, {
    fetchPolicy: 'no-cache',
    onCompleted: (data) => {
      setData(convertJsonStructure(data.companyStatisticsGraph));
    },
  });

  const monthNames = useMemo(
    () => [
      tcommon('Jan'),
      tcommon('Feb'),
      tcommon('Mar'),
      tcommon('Apr'),
      tcommon('May'),
      tcommon('Jun'),
      tcommon('Jul'),
      tcommon('Aug'),
      tcommon('Sep'),
      tcommon('Oct'),
      tcommon('Nov'),
      tcommon('Dec'),
    ],
    [tcommon]
  );

  const getLabel = (value: string) => {
    return getLinkLabel(value?.toLowerCase());
  };

  function convertJsonStructure(input): TotalInstitutionData[] {
    if (!input.length) return [];

    const result = input.flatMap(({ date, values }) => {
      const totalCount = values.reduce((sum, { count }) => sum + count, 0);

      const individualEntries = values.map(({ type, count }) => ({
        date,
        type: getLabel(type),
        count,
      }));

      return [{ date, type: 'All', count: totalCount }, ...individualEntries];
    });

    return result.sort(
      (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
    );
  }

  useEffect(() => {
    if (dates.length) {
      getData({
        variables: {
          input: {
            startDate: dates[0],
            endDate: dates[1],
          },
        },
      });
    }
  }, [dates, getData]);

  const defaultSelectedItems = useMemo(() => {
    if (data.length > 0) {
      return data.map((item) => item.type);
    }
    return [];
  }, [data]);

  useEffect(() => {
    setSelectedItems(defaultSelectedItems);
  }, [defaultSelectedItems]);

  const config = useMemo<LineConfig & RefAttributes<unknown>>(
    () => ({
      data: data,
      xField: 'date',
      yField: 'count',
      seriesField: 'type',
      colorField: 'type',
      loading: loading,
      yAxis: {
        label: {
          style: {
            fontFamily: 'Lufga',
          },
        },
      },
      xAxis: {
        label: {
          style: {
            fontFamily: 'Lufga',
          },
          labelFormatter: (v) => {
            const monthIndex = parseInt(v.split('-')[1], 10) - 1;
            return monthNames[monthIndex];
          },
        },
      },
      legend: {
        color: {
          position: 'bottom',
          autoWrap: true,
          itemMarker: 'square',
          style: {
            fontSize: 14,
            fontFamily: 'Lufga',
          },
          layout: {
            justifyContent: 'center',
            alignItems: 'center',
          },
        },
      },
      onReady: ({ chart }) => {
        chart.on('legend:filter', (e: PlotEvent) => {
          if (e?.data?.values) {
            const values = e?.data?.values as Array<string>;
            setSelectedItems([...values]);
          }
        });
      },
      interactions: [
        {
          type: 'legend-filter',
        },
      ],
      animation: {
        appear: {
          animation: 'path-in',
          duration: 100,
        },
      },
      label: {
        text: 'count',
        position: 'top',
        textAlign: 'center',
        dy: -10,
        style: {
          fontFamily: 'Lufga',
        },
      },
      scale: {
        color: {
          range: COLOR_PLATE_10,
        },
      },
    }),
    [data, loading, monthNames]
  );

  const handleChange: RangePickerProps['onChange'] = (dates, dateStrings) => {
    if (dates) {
      setDates([dateStrings[0], dateStrings[1]]);
    }
  };

  const visibleTotal = useMemo(() => {
    if (selectedItems.includes('All')) {
      return data
        .filter((item) => item.type === 'All')
        .reduce((acc, curr) => acc + curr.count, 0);
    } else {
      return data
        .filter(
          (item) => item.type !== 'All' && selectedItems.includes(item.type)
        )
        .reduce((acc, curr) => acc + curr.count, 0);
    }
  }, [data, selectedItems]);

  return (
    <Card style={{ borderRadius: 20 }}>
      <div className="flex flex-row flex-wrap justify-between">
        <h2 className="flex items-center flex-wrap gap-1">
          <span>{t('total_during_period')}</span>
          <span className="text-gray-700 font-medium">
            / {t('total_all_time')}
          </span>
        </h2>
        <div>
          <RangePicker
            onChange={handleChange}
            defaultValue={[dayjs(dates[0]), dayjs(dates[1])]}
            style={{
              borderRadius: 20,
            }}
          />
        </div>
      </div>
      <div className="flex items-center flex-wrap gap-1 my-4">
        <span className="text-gray-900 text-3xl">{visibleTotal}</span>
        <span className="text-gray-700 text-2xl">
          / {dataTotal?.totalCompanyCount?.toLocaleString()}
        </span>
      </div>
      <Line {...config} />
    </Card>
  );
};

export default TotalCompaniesStats;
